import {
  Box,
  Button,
  Text,
  Flex,
  VStack,
  HStack,
  useToast,
  IconButton,
  Divider,
  Badge,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';
import { FC, useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { TriviaQuestion, TriviaAnswer, TriviaAnswerExtraData, TriviaQuestionExtraData } from '@olagg/db-types';
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaGlobe } from 'react-icons/fa';
import OlaggForm from '../../components/Forms/OlaggForm';

type AnswerFormData = {
  id?: number;
  uid?: string;
  text: string;
  is_correct: boolean;
  position: number;
  text_en?: string;
  text_pt?: string;
  _destroy?: boolean;
};

type TriviaFormData = {
  title: string;
  text: string;
  explanation: string;
  points: number;
  position?: number;
  time_limit_seconds?: number | null;
  active: boolean;
  speed_bonus_enabled: boolean;
  speed_bonus_multiplier: number;
  // Traducciones EN
  title_en: string;
  text_en: string;
  explanation_en: string;
  // Traducciones PT
  title_pt: string;
  text_pt: string;
  explanation_pt: string;
  // Respuestas
  trivia_answers_attributes: AnswerFormData[];
};

type IProps = {
  initialData?: TriviaQuestion;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
};

const TriviaQuestionForm: FC<IProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  isEdit = false
}) => {
  const toast = useToast();
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState<number>(0);

  const useFormMethods = useForm<TriviaFormData>({
    mode: 'onChange',
    defaultValues: {
      title: initialData?.title || '',
      text: initialData?.text || '',
      explanation: initialData?.explanation || '',
      points: initialData?.points || 10,
      position: initialData?.position || undefined,
      time_limit_seconds: initialData?.timeLimitSeconds || 30,
      active: initialData?.active ?? true,
      speed_bonus_enabled: initialData?.speedBonusEnabled ?? false,
      speed_bonus_multiplier: parseFloat(initialData?.speedBonusMultiplier || '1.5'),
      // Traducciones EN desde extraData
      title_en: initialData?.extraData?.en?.title || '',
      text_en: initialData?.extraData?.en?.text || '',
      explanation_en: initialData?.extraData?.en?.explanation || '',
      // Traducciones PT desde extraData
      title_pt: initialData?.extraData?.pt?.title || '',
      text_pt: initialData?.extraData?.pt?.text || '',
      explanation_pt: initialData?.extraData?.pt?.explanation || '',
      // Respuestas
      trivia_answers_attributes: initialData?.triviaAnswers?.map(a => ({
        id: a.id,
        uid: a.uid,
        text: a.text,
        is_correct: a.isCorrect,
        position: a.position,
        text_en: a.extraData?.en?.text || '',
        text_pt: a.extraData?.pt?.text || ''
      })) || [
        { text: '', is_correct: true, position: 1, text_en: '', text_pt: '' },
        { text: '', is_correct: false, position: 2, text_en: '', text_pt: '' },
        { text: '', is_correct: false, position: 3, text_en: '', text_pt: '' },
        { text: '', is_correct: false, position: 4, text_en: '', text_pt: '' }
      ]
    }
  });

  const {
    control,
    watch,
    setValue,
    getValues
  } = useFormMethods;

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'trivia_answers_attributes'
  });

  const speedBonusEnabled = watch('speed_bonus_enabled');

  // Find initial correct answer index
  useEffect(() => {
    if (initialData?.triviaAnswers) {
      const correctIndex = initialData.triviaAnswers.findIndex(a => a.isCorrect);
      if (correctIndex !== -1) {
        setCorrectAnswerIndex(correctIndex);
      }
    }
  }, [initialData]);

  const handleCorrectAnswerChange = (index: number) => {
    setCorrectAnswerIndex(index);
    const currentAnswers = getValues('trivia_answers_attributes');
    currentAnswers.forEach((_, i) => {
      setValue(`trivia_answers_attributes.${i}.is_correct`, i === index);
    });
  };

  const handleAddAnswer = () => {
    const currentAnswers = getValues('trivia_answers_attributes');
    append({
      text: '',
      is_correct: false,
      position: currentAnswers.length + 1,
      text_en: '',
      text_pt: ''
    });
  };

  const handleRemoveAnswer = (index: number) => {
    if (fields.length <= 2) {
      toast({
        title: 'Minimo 2 respuestas',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    if (index === correctAnswerIndex) {
      const newCorrectIndex = index === 0 ? 1 : 0;
      setCorrectAnswerIndex(newCorrectIndex);
      setValue(`trivia_answers_attributes.${newCorrectIndex}.is_correct`, true);
    } else if (index < correctAnswerIndex) {
      setCorrectAnswerIndex(correctAnswerIndex - 1);
    }

    remove(index);

    const updatedAnswers = getValues('trivia_answers_attributes');
    updatedAnswers.forEach((_, i) => {
      setValue(`trivia_answers_attributes.${i}.position`, i + 1);
    });
  };

  const handleMoveAnswer = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    move(index, newIndex);

    if (index === correctAnswerIndex) {
      setCorrectAnswerIndex(newIndex);
    } else if (newIndex === correctAnswerIndex) {
      setCorrectAnswerIndex(index);
    }

    setTimeout(() => {
      const updatedAnswers = getValues('trivia_answers_attributes');
      updatedAnswers.forEach((_, i) => {
        setValue(`trivia_answers_attributes.${i}.position`, i + 1);
      });
    }, 0);
  };

  const beforeSubmit = async (data: TriviaFormData) => {
    // Validaciones
    const hasCorrectAnswer = data.trivia_answers_attributes.some(a => a.is_correct);
    if (!hasCorrectAnswer) {
      toast({
        title: 'Selecciona una respuesta correcta',
        status: 'error',
        duration: 3000
      });
      return;
    }

    const emptyAnswers = data.trivia_answers_attributes.filter(a => !a.text?.trim());
    if (emptyAnswers.length > 0) {
      toast({
        title: 'Todas las respuestas deben tener texto',
        status: 'error',
        duration: 3000
      });
      return;
    }

    if (!data.title?.trim()) {
      toast({
        title: 'El titulo es requerido',
        status: 'error',
        duration: 3000
      });
      return;
    }

    // Construir extra_data con traducciones
    const extra_data: TriviaQuestionExtraData = {};
    if (data.title_en || data.text_en || data.explanation_en) {
      extra_data.en = {
        ...(data.title_en && { title: data.title_en }),
        ...(data.text_en && { text: data.text_en }),
        ...(data.explanation_en && { explanation: data.explanation_en })
      };
    }
    if (data.title_pt || data.text_pt || data.explanation_pt) {
      extra_data.pt = {
        ...(data.title_pt && { title: data.title_pt }),
        ...(data.text_pt && { text: data.text_pt }),
        ...(data.explanation_pt && { explanation: data.explanation_pt })
      };
    }

    // Preparar datos para enviar
    const submitData: any = {
      title: data.title,
      text: data.text || '',
      explanation: data.explanation || '',
      points: data.points,
      active: data.active,
      speed_bonus_enabled: data.speed_bonus_enabled,
      speed_bonus_multiplier: data.speed_bonus_enabled ? data.speed_bonus_multiplier : null,
      time_limit_seconds: data.time_limit_seconds || null,
      extra_data,
      trivia_answers_attributes: data.trivia_answers_attributes.map((answer, index) => {
        const answerExtraData: TriviaAnswerExtraData = {};
        if (answer.text_en) {
          answerExtraData.en = { text: answer.text_en };
        }
        if (answer.text_pt) {
          answerExtraData.pt = { text: answer.text_pt };
        }

        return {
          ...(answer.id ? { id: answer.id } : {}),
          text: answer.text,
          is_correct: answer.is_correct,
          position: index + 1,
          extra_data: answerExtraData
        };
      })
    };

    if (data.position) {
      submitData.position = data.position;
    }

    await onSubmit(submitData);
  };

  return (
    <OlaggForm
      onSubmit={beforeSubmit}
      useFormMethods={useFormMethods}
      style={{ width: '100%' }}
    >
      <VStack color={'white'} w={'full'} maxW='900px' margin='0 auto' spacing={6}>
        {/* Contenido Principal */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" fontSize="lg" mb={4}>Contenido de la Pregunta</Text>

          <Tabs variant="enclosed" colorScheme="purple">
            <TabList>
              <Tab>Espanol (ES)</Tab>
              <Tab><FaGlobe style={{ marginRight: '8px' }} /> English (EN)</Tab>
              <Tab><FaGlobe style={{ marginRight: '8px' }} /> Portugues (PT)</Tab>
            </TabList>

            <TabPanels>
              {/* Tab Espanol */}
              <TabPanel px={0}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Titulo</FormLabel>
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Ej: En que ano se creo Bitcoin?"
                          bg="gray.700"
                          borderColor="gray.600"
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Pregunta / Subtitulo (opcional)</FormLabel>
                    <Controller
                      name="text"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Ej: Selecciona el ano correcto"
                          bg="gray.700"
                          borderColor="gray.600"
                          rows={2}
                        />
                      )}
                    />
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      Texto adicional que se muestra debajo del titulo
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Explicacion (opcional)</FormLabel>
                    <Controller
                      name="explanation"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Ej: Bitcoin fue creado en 2009 por Satoshi Nakamoto"
                          bg="gray.700"
                          borderColor="gray.600"
                          rows={3}
                        />
                      )}
                    />
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      Se muestra despues de responder la pregunta
                    </Text>
                  </FormControl>
                </VStack>
              </TabPanel>

              {/* Tab English */}
              <TabPanel px={0}>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Title (EN)</FormLabel>
                    <Controller
                      name="title_en"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Ex: What year was Bitcoin created?"
                          bg="gray.700"
                          borderColor="gray.600"
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Question / Subtitle (EN)</FormLabel>
                    <Controller
                      name="text_en"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Ex: Select the correct year"
                          bg="gray.700"
                          borderColor="gray.600"
                          rows={2}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Explanation (EN)</FormLabel>
                    <Controller
                      name="explanation_en"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Ex: Bitcoin was created in 2009 by Satoshi Nakamoto"
                          bg="gray.700"
                          borderColor="gray.600"
                          rows={3}
                        />
                      )}
                    />
                  </FormControl>
                </VStack>
              </TabPanel>

              {/* Tab Portugues */}
              <TabPanel px={0}>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Titulo (PT)</FormLabel>
                    <Controller
                      name="title_pt"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Ex: Em que ano o Bitcoin foi criado?"
                          bg="gray.700"
                          borderColor="gray.600"
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Pergunta / Subtitulo (PT)</FormLabel>
                    <Controller
                      name="text_pt"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Ex: Selecione o ano correto"
                          bg="gray.700"
                          borderColor="gray.600"
                          rows={2}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Explicacao (PT)</FormLabel>
                    <Controller
                      name="explanation_pt"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Ex: Bitcoin foi criado em 2009 por Satoshi Nakamoto"
                          bg="gray.700"
                          borderColor="gray.600"
                          rows={3}
                        />
                      )}
                    />
                  </FormControl>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        {/* Configuracion */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" fontSize="lg" mb={4}>Configuracion</Text>

          <HStack w="full" spacing={4} mb={4}>
            <FormControl flex={1}>
              <FormLabel>Puntos</FormLabel>
              <Controller
                name="points"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    {...field}
                    min={1}
                    max={1000}
                    onChange={(_, val) => field.onChange(val)}
                  >
                    <NumberInputField bg="gray.700" borderColor="gray.600" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                )}
              />
            </FormControl>

            <FormControl flex={1}>
              <FormLabel>Posicion</FormLabel>
              <Controller
                name="position"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    {...field}
                    min={1}
                    onChange={(_, val) => field.onChange(val || undefined)}
                  >
                    <NumberInputField
                      bg="gray.700"
                      borderColor="gray.600"
                      placeholder="Auto"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                )}
              />
            </FormControl>
          </HStack>

          <HStack w="full" spacing={4}>
            <FormControl flex={1}>
              <FormLabel>Tiempo limite (segundos)</FormLabel>
              <Controller
                name="time_limit_seconds"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    {...field}
                    value={field.value || ''}
                    min={5}
                    max={300}
                    onChange={(_, val) => field.onChange(val || null)}
                  >
                    <NumberInputField
                      bg="gray.700"
                      borderColor="gray.600"
                      placeholder="Sin limite"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                )}
              />
            </FormControl>

            <FormControl flex={1} display="flex" alignItems="center" pt={8}>
              <FormLabel mb={0} mr={4}>Activa</FormLabel>
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <Switch
                    isChecked={field.value}
                    onChange={field.onChange}
                    colorScheme="green"
                  />
                )}
              />
            </FormControl>
          </HStack>
        </Box>

        {/* Speed Bonus */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <HStack justify="space-between" mb={speedBonusEnabled ? 4 : 0}>
            <Text fontWeight="bold">Bonus por velocidad</Text>
            <Controller
              name="speed_bonus_enabled"
              control={control}
              render={({ field }) => (
                <Switch
                  isChecked={field.value}
                  onChange={field.onChange}
                  colorScheme="yellow"
                />
              )}
            />
          </HStack>

          {speedBonusEnabled && (
            <FormControl>
              <FormLabel>Multiplicador</FormLabel>
              <Controller
                name="speed_bonus_multiplier"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    {...field}
                    min={1}
                    max={10}
                    step={0.1}
                    precision={1}
                    onChange={(_, val) => field.onChange(val)}
                  >
                    <NumberInputField bg="gray.700" borderColor="gray.600" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                )}
              />
              <Text fontSize="xs" color="gray.400" mt={1}>
                Los puntos se multiplican por este valor para respuestas rapidas
              </Text>
            </FormControl>
          )}
        </Box>

        <Divider />

        {/* Respuestas */}
        <Box w="full">
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight="bold" fontSize="lg">Respuestas</Text>
            <Button
              size="sm"
              leftIcon={<FaPlus />}
              colorScheme="purple"
              onClick={handleAddAnswer}
              isDisabled={fields.length >= 6}
            >
              Agregar respuesta
            </Button>
          </Flex>

          <Text fontSize="sm" color="gray.400" mb={4}>
            Selecciona la respuesta correcta haciendo clic en el circulo. Expande cada respuesta para agregar las traducciones (EN/PT).
          </Text>

          <VStack spacing={3}>
            {fields.map((field, index) => (
              <Accordion
                key={field.id}
                allowToggle
                w="full"
              >
                <AccordionItem
                  border="2px solid"
                  borderColor={index === correctAnswerIndex ? 'green.500' : 'gray.600'}
                  borderRadius="md"
                  bg={index === correctAnswerIndex ? 'green.900' : 'gray.700'}
                >
                  <Box>
                    <HStack p={4} spacing={4}>
                      <Badge colorScheme="purple" fontSize="md" px={2}>
                        {index + 1}
                      </Badge>

                      <Box
                        as="button"
                        type="button"
                        w={6}
                        h={6}
                        borderRadius="full"
                        border="2px solid"
                        borderColor={index === correctAnswerIndex ? 'green.500' : 'gray.500'}
                        bg={index === correctAnswerIndex ? 'green.500' : 'transparent'}
                        onClick={() => handleCorrectAnswerChange(index)}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        _hover={{ borderColor: 'green.400' }}
                      >
                        {index === correctAnswerIndex && (
                          <Box w={3} h={3} borderRadius="full" bg="white" />
                        )}
                      </Box>

                      <Controller
                        name={`trivia_answers_attributes.${index}.text`}
                        control={control}
                        render={({ field: inputField }) => (
                          <Input
                            {...inputField}
                            placeholder={`Respuesta ${index + 1} (ES)`}
                            bg="gray.800"
                            borderColor="gray.600"
                            flex={1}
                          />
                        )}
                      />

                      <HStack spacing={1}>
                        <AccordionButton
                          as={IconButton}
                          aria-label="Traduccion"
                          icon={<FaGlobe />}
                          size="sm"
                          variant="ghost"
                          w="auto"
                          h="auto"
                          p={2}
                        />
                        <IconButton
                          aria-label="Mover arriba"
                          icon={<FaArrowUp />}
                          size="sm"
                          variant="ghost"
                          isDisabled={index === 0}
                          onClick={() => handleMoveAnswer(index, 'up')}
                        />
                        <IconButton
                          aria-label="Mover abajo"
                          icon={<FaArrowDown />}
                          size="sm"
                          variant="ghost"
                          isDisabled={index === fields.length - 1}
                          onClick={() => handleMoveAnswer(index, 'down')}
                        />
                        <IconButton
                          aria-label="Eliminar"
                          icon={<FaTrash />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleRemoveAnswer(index)}
                          isDisabled={fields.length <= 2}
                        />
                      </HStack>
                    </HStack>

                    {index === correctAnswerIndex && (
                      <Badge colorScheme="green" ml={4} mb={2}>
                        Respuesta correcta
                      </Badge>
                    )}
                  </Box>

                  <AccordionPanel pb={4} bg="gray.800" borderBottomRadius="md">
                    <VStack spacing={3} align="stretch">
                      <FormControl>
                        <FormLabel fontSize="sm">
                          <HStack spacing={2}>
                            <FaGlobe />
                            <Text>Answer (EN)</Text>
                          </HStack>
                        </FormLabel>
                        <Controller
                          name={`trivia_answers_attributes.${index}.text_en`}
                          control={control}
                          render={({ field: inputField }) => (
                            <Input
                              {...inputField}
                              placeholder={`Answer ${index + 1} in English`}
                              bg="gray.700"
                              borderColor="gray.600"
                            />
                          )}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">
                          <HStack spacing={2}>
                            <FaGlobe />
                            <Text>Resposta (PT)</Text>
                          </HStack>
                        </FormLabel>
                        <Controller
                          name={`trivia_answers_attributes.${index}.text_pt`}
                          control={control}
                          render={({ field: inputField }) => (
                            <Input
                              {...inputField}
                              placeholder={`Resposta ${index + 1} em Portugues`}
                              bg="gray.700"
                              borderColor="gray.600"
                            />
                          )}
                        />
                      </FormControl>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            ))}
          </VStack>
        </Box>

        {/* Botones de accion */}
        <HStack w="full" spacing={4} pt={4}>
          <Button
            variant="outline"
            onClick={onCancel}
            flex={1}
          >
            Cancelar
          </Button>
          <Button
            variant="filledGradient"
            type="submit"
            flex={1}
            isLoading={loading}
            loadingText={isEdit ? 'Actualizando...' : 'Creando...'}
          >
            {isEdit ? 'Actualizar Pregunta' : 'Crear Pregunta'}
          </Button>
        </HStack>
      </VStack>
    </OlaggForm>
  );
};

export default TriviaQuestionForm;
